/* =============================================================
   Quick Add — JavaScript (Web Component style)
   Usage: attach data-quick-add + data-product-handle (+ optionally
          data-variant-id, data-has-only-default) to any button.
   ============================================================= */
import { formatMoney } from 'util.misc';

if (!customElements.get('quick-add-modal')) {
  class QuickAddModal extends HTMLElement {
    constructor() {
      super();
    }

    initProduct() {
      var jsonEl = this.querySelector('[data-product-json]');
      if (!jsonEl) {
        this.product = null;
        return;
      }
      try {
        this.product = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('Failed to parse product JSON', e);
        this.product = null;
      }

      this.overlay = this.querySelector('.qam__overlay');
      this.panel = this.querySelector('.qam__panel');
      this.body = this.querySelector('.qam__body');
      this.footer = this.querySelector('.qam__footer');
      this.atcBtn = this.querySelector('.add-to-cart');
      this.closeBtn = this.querySelector('[data-close-modal]');
      this.errorEl = this.querySelector('[data-error-element]');
      this.priceCurrent = this.querySelector('[data-current-price]');
      this.priceCompare = this.querySelector('[data-compare-price]');
      this.stockBadge = this.querySelector('[data-stock-badge]');

      this.addToCartText = this.getAttribute('data-add-to-cart-text') || 'Add to Cart';
      this.soldOutText = this.getAttribute('data-sold-out-text') || 'Sold Out';
      this.inStockText = this.getAttribute('data-in-stock-text') || 'In Stock';

      // Find initially selected options based on active classes in Liquid
      this.selectedOptions = [];
      var initialSelectedBtns = this.querySelectorAll('[data-option-index].is-selected');
      initialSelectedBtns.forEach(btn => {
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-option-value');
        this.selectedOptions[idx] = val;
      });

      // Fill in gaps if option index is not selected (default to first variant)
      if (this.product && this.product.variants && this.product.variants[0]) {
        var firstVar = this.product.variants[0];
        for (var i = 0; i < firstVar.options.length; i++) {
          if (this.selectedOptions[i] === undefined) {
            this.selectedOptions[i] = firstVar.options[i];
          }
        }
      }
    }

    connectedCallback() {
      this.initProduct();
      this.bindEvents();

      // Listen for esc key
      this.escHandler = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.escHandler);
    }

    bindEvents() {
      // Bind close triggers
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-close-modal]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });

      // Bind option clicks
      this.querySelectorAll('[data-option-index]').forEach(btn => {
        btn.addEventListener('click', () => {
          var idx = parseInt(btn.getAttribute('data-option-index'), 10);
          var val = btn.getAttribute('data-option-value');
          this.selectedOptions[idx] = val;
          this.updateOptionsUI();
        });
      });

      // Size guide binding
      var sizeGuideBtn = this.querySelector('[data-open-size-guide]');
      if (sizeGuideBtn) {
        sizeGuideBtn.addEventListener('click', () => {
          var sizeGuideEl = document.querySelector('.variant__label-info--size-guide');
          if (sizeGuideEl) {
            sizeGuideEl.click();
          }
        });
      }

      // ATC button click
      if (this.atcBtn) {
        var newAtc = this.atcBtn.cloneNode(true);
        this.atcBtn.parentNode.replaceChild(newAtc, this.atcBtn);
        this.atcBtn = newAtc;

        this.atcBtn.addEventListener('click', () => {
          var variantId = parseInt(this.atcBtn.getAttribute('data-variant-id'), 10);
          if (variantId) this.addToCart(variantId, this.atcBtn);
        });
      }
    }

    disconnectedCallback() {
      if (this.escHandler) {
        document.removeEventListener('keydown', this.escHandler);
      }
    }

    open() {
      window.dispatchEvent(new CustomEvent('popover:close'));
      this.classList.add('is-open');
      document.body.classList.add('qam-open');
      if (this.atcBtn) this.atcBtn.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('qam-open');
      this.clearError();
    }

    findVariant(opts) {
      if (!this.product) return null;
      const variant = (this.product.variants || []).find(v => {
        return v.options.every((o, i) => o === opts[i]);
      });
      return variant || null;
    }

    updateOptionsUI() {
      var variant = this.findVariant(this.selectedOptions);

      // Update option buttons classes
      this.querySelectorAll('[data-option-index]').forEach(btn => {
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-option-value');
        var isSelected = this.selectedOptions[idx] === val;

        btn.classList.toggle('is-selected', isSelected);

        // Check availability dynamically
        var testOpts = this.selectedOptions.slice();
        testOpts[idx] = val;
        var matchVariant = this.findVariant(testOpts);
        var isAvailable = matchVariant && matchVariant.available;
        btn.classList.toggle('is-unavailable', !isAvailable);
      });

      const locale = window.locale || (window.theme && window.theme.settings && window.theme.settings.locale) || `${window.Shopify.locale}-${window.Shopify.country}`;
      const currency = window.currency || (window.theme && window.theme.settings && window.theme.settings.currency) || window.Shopify.currency.active;

      // Update prices
      if (this.priceCurrent) {
        this.priceCurrent.textContent = variant 
          ? formatMoney({ locale, currency, value: variant.price }) 
          : formatMoney({ locale, currency, value: 0 });
      }
      if (this.priceCompare) {
        if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.priceCompare.textContent = formatMoney({ locale, currency, value: variant.compare_at_price });
          this.priceCompare.style.display = '';
        } else {
          this.priceCompare.style.display = 'none';
        }
      }

      // Update stock badge
      var available = variant && variant.available;
      if (this.stockBadge) {
        this.stockBadge.classList.toggle('is-sold-out', !available);
        this.stockBadge.textContent = available ? this.inStockText : this.soldOutText;
      }

      // Update ATC button
      if (this.atcBtn) {
        this.atcBtn.disabled = !available;
        this.atcBtn.setAttribute('data-variant-id', variant ? variant.id : '');
        this.atcBtn.textContent = available ? this.addToCartText : this.soldOutText;
      }
    }

    addToCart(variantId, buttonEl) {
      buttonEl.classList.add('kit-button--loading');
      buttonEl.disabled = true;
      this.clearError();

      var cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd)
        ? window.theme.routes.cartAdd
        : '/cart/add.js';

      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
      .then(r => r.json())
      .then(data => {
        buttonEl.classList.remove('kit-button--loading');
        buttonEl.disabled = false;

        if (data.status === 422 || data.description) {
          this.showError(data.description || 'Could not add to cart.');
          return;
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: { product: data, addToCartBtn: buttonEl }
        }));

        this.close();
      })
      .catch(() => {
        buttonEl.classList.remove('kit-button--loading');
        buttonEl.disabled = false;
        this.showError('Something went wrong. Please try again.');
      });
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.classList.add('is-visible');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.classList.remove('is-visible');
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  // Register Custom Element
  customElements.define('quick-add-modal', QuickAddModal);
}

/* ─── Click delegation — any [data-quick-add] anywhere ─────── */
if (!window.quickAddClickDelegationBound) {
  window.quickAddClickDelegationBound = true;
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add]');
    if (!trigger) return;
    window.dispatchEvent(new CustomEvent('popover:close'));
    e.preventDefault();

    var handle         = trigger.getAttribute('data-product-handle');
    var variantId      = parseInt(trigger.getAttribute('data-variant-id'), 10);
    var hasOnlyDefault = trigger.getAttribute('data-has-only-default') === 'true';

    if (!handle && !variantId) return;

    if (hasOnlyDefault && variantId) {
      trigger.classList.add('kit-button--loading');
      trigger.disabled = true;

      var cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd)
        ? window.theme.routes.cartAdd
        : '/cart/add.js';

      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        trigger.classList.remove('kit-button--loading');
        trigger.disabled = false;

        if (data.status === 422 || data.description) {
          alert(data.description || 'Could not add to cart.');
          return;
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: { product: data, addToCartBtn: trigger }
        }));
      })
      .catch(function () {
        trigger.classList.remove('kit-button--loading');
        trigger.disabled = false;
        alert('Something went wrong. Please try again.');
      });
      return;
    }

    var section = trigger.closest('.shopify-section');
    var modalEl = null;
    if (section) {
      modalEl = section.querySelector('quick-add-modal[data-product-handle="' + handle + '"]');
    }
    if (!modalEl) {
      modalEl = document.querySelector('quick-add-modal[data-product-handle="' + handle + '"]');
    }

    if (modalEl) {
      modalEl.open();
    } else {
      window.location = '/products/' + handle;
    }
  });
}
